import * as THREE from 'three';

import type { RichSatellite, SatelliteKind } from '../data/types';
import { ecefToScene } from '../lib/geo3d';
import { ISS_NORAD_ID } from './satelliteGlobeConstants';
import {
  createSelectedSatelliteSprite,
  disposeSelectedSatelliteSprite,
  type SatelliteIconAtlas,
} from './satelliteGlobeAssets';

const KINDS = Object.keys({
  starlink: 1,
  station: 1,
  navigation: 1,
  agency: 1,
  military: 1,
  geocomms: 1,
  constellation: 1,
  debris: 1,
}) as SatelliteKind[];

const MAX_PER_LAYER = 20_000;
const WATCH_SCALE = 0.075;
const SELECTED_SCALE = 0.1;

export interface SatelliteKindLayers {
  group: THREE.Group;
  update: (satellites: RichSatellite[], selectedNoradId: string | null) => void;
  dispose: () => void;
}

export function createSatelliteKindLayers(
  atlas: SatelliteIconAtlas,
  isMobile: boolean,
): SatelliteKindLayers {
  const group = new THREE.Group();
  const iconSize = isMobile ? 0.028 : 0.024;

  const layerByKind = new Map<
    SatelliteKind,
    {
      geometry: THREE.BufferGeometry;
      position: THREE.BufferAttribute;
      points: THREE.Points;
      material: THREE.PointsMaterial;
    }
  >();

  for (const kind of KINDS) {
    const geometry = new THREE.BufferGeometry();
    const position = new THREE.BufferAttribute(new Float32Array(MAX_PER_LAYER * 3), 3);
    position.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', position);
    geometry.setDrawRange(0, 0);

    const texture = atlas.textures.get(kind) ?? atlas.textures.get('debris');
    const material = new THREE.PointsMaterial({
      map: texture,
      size: iconSize,
      color: 0xffffff,
      transparent: true,
      opacity: 0.95,
      sizeAttenuation: true,
      depthWrite: true,
      depthTest: true,
      alphaTest: 0.08,
    });

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    group.add(points);
    layerByKind.set(kind, { geometry, position, points, material });
  }

  const overlayGroup = new THREE.Group();
  group.add(overlayGroup);

  let watchSprite: THREE.Sprite | null = null;
  let watchId: string | null = null;
  let selectedSprite: THREE.Sprite | null = null;
  let selectedId: string | null = null;

  const clearSprite = (sprite: THREE.Sprite | null) => {
    if (!sprite) return;
    disposeSelectedSatelliteSprite(sprite);
  };

  const clearOverlays = () => {
    clearSprite(watchSprite);
    clearSprite(selectedSprite);
    watchSprite = null;
    selectedSprite = null;
    watchId = null;
    selectedId = null;
    overlayGroup.clear();
  };

  const placeSprite = (sat: RichSatellite, scale: number, tint?: number) => {
    const sprite = createSelectedSatelliteSprite(atlas, sat.kind, scale);
    if (tint != null) sprite.material.color.setHex(tint);
    const [x, y, z] = ecefToScene(sat.position.ecef);
    sprite.position.set(x, y, z);
    overlayGroup.add(sprite);
    return sprite;
  };

  const counts = new Map<SatelliteKind, number>();
  for (const kind of KINDS) counts.set(kind, 0);

  const update = (satellites: RichSatellite[], selectedNoradId: string | null) => {
    for (const kind of KINDS) counts.set(kind, 0);

    const skipIds = new Set<string>();
    if (selectedNoradId) skipIds.add(selectedNoradId);
    if (watchId) skipIds.add(watchId);

    for (const sat of satellites) {
      if (skipIds.has(sat.noradId)) continue;
      const kind = layerByKind.has(sat.kind) ? sat.kind : 'debris';
      const layer = layerByKind.get(kind)!;
      let idx = counts.get(kind) ?? 0;
      if (idx >= MAX_PER_LAYER) continue;
      const [x, y, z] = ecefToScene(sat.position.ecef);
      layer.position.setXYZ(idx, x, y, z);
      idx++;
      counts.set(kind, idx);
    }

    for (const kind of KINDS) {
      const layer = layerByKind.get(kind)!;
      const count = counts.get(kind) ?? 0;
      layer.geometry.setDrawRange(0, count);
      layer.position.needsUpdate = true;
    }

    const iss = satellites.find((s) => s.noradId === ISS_NORAD_ID);
    if (iss) {
      if (watchId !== ISS_NORAD_ID) {
        clearSprite(watchSprite);
        watchSprite = placeSprite(iss, WATCH_SCALE, 0xfbbf24);
        watchId = ISS_NORAD_ID;
      } else if (watchSprite) {
        const [x, y, z] = ecefToScene(iss.position.ecef);
        watchSprite.position.set(x, y, z);
      }
    } else if (watchSprite) {
      clearSprite(watchSprite);
      watchSprite = null;
      watchId = null;
    }

    if (selectedNoradId !== selectedId) {
      clearSprite(selectedSprite);
      selectedSprite = null;
      selectedId = null;
    }

    const selected = satellites.find((s) => s.noradId === selectedNoradId);
    if (selected) {
      if (!selectedSprite) {
        selectedSprite = placeSprite(selected, SELECTED_SCALE);
        selectedId = selectedNoradId;
      } else {
        const [x, y, z] = ecefToScene(selected.position.ecef);
        selectedSprite.position.set(x, y, z);
      }
    } else if (selectedSprite) {
      clearSprite(selectedSprite);
      selectedSprite = null;
      selectedId = null;
    }
  };

  return {
    group,
    update,
    dispose: () => {
      clearOverlays();
      for (const layer of layerByKind.values()) {
        layer.geometry.dispose();
        layer.material.dispose();
      }
    },
  };
}
